import { faEdit, faPauseCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, FormControl, InputLabel, MenuItem } from '@mui/material';
import { format } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import usePresentations from '../../../../../../../hooks/usePresentations';
import { Meetingroom, Slot } from '../../../../../../../interfaces/interfaces';
import { useSelector } from '../../../../../../../reduxToolkit/typedRedux';
import { MeetingConfigButton } from '../../../../../../account/Booth/BoothInfo/MeetingConfigButton';
import { PresentationGuestList } from '../../../../../../account/Booth/BoothPresentations/PresentationGuestList';
import PresentationInfosButton from '../../../../../../account/Booth/BoothPresentations/PresentationInfosButton';
import VyAnimatedFontAwesomeIconButton from '../../../../../../common/VyAnimatedFontAwesomeIconButton';
import VyDataGrid, { VyColDef } from '../../../../../../common/VyDataGrid';
import VyModal from '../../../../../../common/VyModal';
import VySelect from '../../../../../../common/VySelect';
import VyTooltip from '../../../../../../common/VyTooltip';
import PresentationTypeWithTooltip from '../PresentationTypeWithToopltip';
import NewSlotGroup from './newSlotGroup';

const DEFAULT_LIMIT = 25;

interface SlotGroup {
  key: string;
  slots: Slot[];
  datetime_start: string;
  datetime_end: string;
}

interface PresentationsListViewInterafce {
  eventid?: string;
  onDragEnd: (result: any) => Promise<void>;
  unassignedSlots: Slot[];
  onSlotGroupCreate: (
    time: {
      datetime_start: string;
      datetime_end: string;
    },
    withFetch?: boolean | undefined
  ) => void;
  slotGroups: SlotGroup[];
  markedSlots: SlotGroup[];
  makeGroupName(datetime_start: string, datetime_end: string): string;
  onSlotGroupDelete: (key: string) => void;
  onEditPresentation: (meetingroom: Meetingroom) => void;
  fetch: (onlyUnassigned?: boolean) => void;
}

const PresentationsListView = ({ unassignedSlots, slotGroups, onSlotGroupCreate, fetch, onEditPresentation, eventid }: PresentationsListViewInterafce) => {
  const { t } = useTranslation();
  const { remindHost, removeSlot, updateGroup, assignSlot } = usePresentations();

  const [slots, setSlots] = useState<(Slot & { groupKey?: string })[]>([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [isNewSlotGroup, setIsNewSlotGroup] = useState(false);

  const event = useSelector((state) => state.admin.event);

  useEffect(() => {
    setIsLoading(true);
    const _slots = [...unassignedSlots];
    for (const group of slotGroups) {
      _slots.push(...group.slots.map((s) => ({ ...s, groupKey: group.key })));
    }
    setSlots(_slots);
    setTotal(_slots.length);
    setIsLoading(false);
  }, [unassignedSlots, slotGroups]);

  const handleGroupChange = async (value: string, slot: Slot) => {
    setIsLoading(true);
    if (value === 'new') {
      setIsNewSlotGroup(true);
      return;
    }
    if (value === 'unassigned') {
      await removeSlot(slot._id);
    } else {
      const datetime_start = value.substring(0, value.indexOf(' | '));
      const datetime_end = value.substring(value.indexOf(' | ') + 3, value.lastIndexOf(' | '));
      const groupKey = value.substring(value.lastIndexOf(' | ') + 3);
      const newSlot: Slot = { ...slot, datetime_start, datetime_end, sortIndex: slotGroups.find((g) => g.key === groupKey)?.slots.length ?? 0 };
      if (slot.status === 'unassigned') {
        await assignSlot(newSlot);
      } else {
        await updateGroup([newSlot]);
      }
    }
    fetch();
  };

  const columns: VyColDef[] = [
    {
      field: 'groupKey',
      headerName: t('account:slots.group'),
      width: 250,
      sortable: false,
      editable: false,
      renderCell: (r) => (
        <FormControl fullWidth>
          <InputLabel id="group-key-select-label">{t('account:slots.group')}</InputLabel>
          <VySelect
            labelId="group-key-select-label"
            id="group-key-select"
            value={r.row.groupKey ? r.row.datetime_start + ' | ' + r.row.datetime_end + ' | ' + r.row.groupKey : 'unassigned'}
            label={t('account:slots.group')}
            onChange={(e) => handleGroupChange(e.target.value as string, r.row)}>
            <MenuItem value={'unassigned'}>{t('account:slots.unassigned')}</MenuItem>
            {slotGroups.map((g) => (
              <MenuItem key={g.key} value={g.datetime_start.toString() + ' | ' + g.datetime_end.toString() + ' | ' + g.key}>
                <p>
                  <b>{format(new Date(g.datetime_start), 'dd.MM.yyyy')}</b>
                  &nbsp; &nbsp;
                  {g.key}
                </p>
              </MenuItem>
            ))}
            <MenuItem value={'new'}>
              <Button>{t('account:slots.create-new-group')}</Button>
            </MenuItem>
          </VySelect>
        </FormControl>
      ),
    },
    {
      field: 'name',
      headerName: t('account:slots.name'),
      width: 250,
      sortable: false,
      editable: false,
      renderCell: (r) => r?.row?.meetingroom?.name,
    },
    {
      field: 'booth',
      headerName: t('account:slots.booth'),
      width: 250,
      sortable: false,
      editable: false,
      renderCell: (r) => r?.row?.meetingroom?.booth?.name,
    },
    {
      field: 'contactperson',
      headerName: t('account:slots.contact-person'),
      width: 250,
      sortable: false,
      editable: false,
      renderCell: (r) => r?.row?.meetingroom?.contactperson,
    },
    {
      field: 'type',
      headerName: t('account:slots.type'),
      width: 250,
      sortable: false,
      editable: false,
      renderCell: (r) => <PresentationTypeWithTooltip slot={r.row} className="text-black cursor-default" />,
    },
    {
      field: 'action',
      headerName: t('account:slots.actions'),
      width: 250,
      sortable: false,
      editable: false,
      renderCell: (r) => (
        <div className="flex flex-row items-center space-x-2">
          {r.row.meetingroom?.booth && !r.row.datetime_hostnotification_sent && !r.row.meetingroom.isStudioForPresentationSlot && (
            <VyAnimatedFontAwesomeIconButton
              style={{ marginLeft: '0.5rem', marginRight: '0.5rem' }}
              tooltip={t('account:slots.slot-not-reminded') || ''}
              icon={faPauseCircle}
              color="black"
              onClick={() => remindHost(r.row._id, eventid)}
              hideIconAfterPromise
            />
          )}
          <VyAnimatedFontAwesomeIconButton
            style={{ marginLeft: '0.5rem', marginRight: '0.5rem' }}
            tooltip={t('account:slots.delete-slot') || ''}
            icon={faTrash}
            color="black"
            onClick={() => removeSlot(r.row._id, eventid)}
          />

          <VyTooltip title={t('account:edit-presentation') || ''}>
            <FontAwesomeIcon
              style={{ color: 'black', cursor: 'pointer', marginLeft: '0.5rem', marginRight: '0.5rem' }}
              icon={faEdit}
              className="cursor-pointer"
              onClick={() => onEditPresentation(r.row.meetingroom)}
            />
          </VyTooltip>
          <PresentationInfosButton meetingroom={r.row.meetingroom} color="black" useAnimatedIconBtn />
          <MeetingConfigButton meetingroom={r.row.meetingroom} slot={r.row} color="black" useAnimatedIconBtn />
          <PresentationGuestList slot={r.row} color="black" />
        </div>
      ),
    },
  ];

  useEffect(() => {
    fetch();
  }, []);

  return (
    <>
      <VyDataGrid
        pagination
        rows={slots}
        columns={columns}
        disableRowSelectionOnClick
        onPaginationModelChange={({ pageSize, page }) => {
          setLimit(pageSize);
          setPage(page);
        }}
        loading={isLoading}
        rowCount={total}
        paginationModel={{ pageSize: limit, page: page }}
      />
      <VyModal open={isNewSlotGroup} onClose={() => setIsNewSlotGroup(false)}>
        {(onClose) => (
          <NewSlotGroup
            onCreate={(time, withFetch) => {
              onSlotGroupCreate(time, withFetch);
              onClose();
            }}
            presentations={unassignedSlots}
          />
        )}
      </VyModal>
    </>
  );
};
export default PresentationsListView;
